if (typeof ProductCashback !== 'function') {
  class ProductCashback extends HTMLElement {
    connectedCallback() {
      this.config = this.readConfig();

      if (!this.config) {
        this.hidden = true;
        return;
      }

      this.messageEl = this.querySelector('[data-product-cashback-message]');

      if (!this.messageEl) {
        this.messageEl = document.createElement('p');
        this.messageEl.className = 'product-cashback__message';
        this.messageEl.setAttribute('data-product-cashback-message', '');
        const configScript = this.querySelector('[data-product-cashback-config]');
        this.insertBefore(this.messageEl, configScript || null);
      }

      const initialAmount = Number(this.dataset.initialAmount);
      if (initialAmount > 0) {
        this.updateMessage(initialAmount);
      }

      this.productVariants = this.closest('product-page')?.querySelector('product-variants');

      if (this.productVariants) {
        this.onVariantChange = this.onVariantChange.bind(this);
        this.productVariants.addEventListener('VARIANT_CHANGE', this.onVariantChange);

        if (this.productVariants.currentVariant?.price) {
          this.updateMessage(this.productVariants.currentVariant.price);
        }
      }
    }

    disconnectedCallback() {
      this.productVariants?.removeEventListener('VARIANT_CHANGE', this.onVariantChange);
    }

    readConfig() {
      const script = this.querySelector('[data-product-cashback-config]');

      if (!script) {
        return null;
      }

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
    }

    onVariantChange(event) {
      const variant = event.target?.currentVariant;

      if (!variant?.price) {
        this.hidden = true;
        return;
      }

      this.updateMessage(variant.price);
    }

    updateMessage(amountCents) {
      const result = this.calculateCashback(amountCents);

      if (!result) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.messageEl.textContent = this.config.message
        .replace(/\{\{\s*cashback_amount\s*\}\}/g, this.formatMoney(result.cashbackCents))
        .replace(/\{\{\s*cashback_percent\s*\}\}/g, `${result.percent}%`);
    }

    calculateCashback(amountCents) {
      const amount = Number(amountCents);

      if (!amount || amount <= 0) {
        return null;
      }

      let percent = 0;

      if (this.config.mode === 'progressive') {
        const tiers = this.config.tiers;

        if (amount <= tiers[0].max) {
          percent = tiers[0].percent;
        } else if (amount <= tiers[1].max) {
          percent = tiers[1].percent;
        } else if (amount <= tiers[2].max) {
          percent = tiers[2].percent;
        } else {
          percent = tiers[3].percent;
        }
      } else {
        percent = Number(this.config.fixedPercent);
      }

      if (!percent || percent <= 0) {
        return null;
      }

      const cashbackCents = Math.floor((amount * percent) / 100);

      if (cashbackCents <= 0) {
        return null;
      }

      return { percent, cashbackCents };
    }

    formatMoney(cents) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      const format = window.KROWN?.settings?.shop_money_format || '{{amount}}';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholderRegex);

      if (!match) {
        return (cents / 100).toFixed(2);
      }

      const formatWithDelimiters = (number, precision, thousands, decimal) => {
        if (Number.isNaN(number) || number === null) {
          return 0;
        }

        const fixed = (number / 100).toFixed(precision);
        const parts = fixed.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        const centsPart = parts[1] ? `${decimal}${parts[1]}` : '';

        return dollars + centsPart;
      };

      let value = '';

      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2, ',', '.');
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        default:
          value = formatWithDelimiters(cents, 2, ',', '.');
      }

      return format.replace(placeholderRegex, value);
    }
  }

  if (typeof customElements.get('product-cashback') === 'undefined') {
    customElements.define('product-cashback', ProductCashback);
  }
}
